import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import CustomDropdown from "../../components/forms/CustomDropdown";
import { nacidEventsApi } from "../../services/nacidEvents/nacidEventsApi";
import { NacidEventResponse, StudentEventRequest } from "../../models/dtos";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import CustomInputText from "../../components/forms/CustomInputText";
import { Toast } from "primereact/toast";
import { useRef } from "react";

interface StudentEventDialogProps {
  visible: boolean;
  onHide: () => void;
  studentId: number;
}

const initialValues: Omit<StudentEventRequest, "studentId"> = {
  nacidEventId: 0,
  documentNumber: null,
  documentDate: null,
  academicYearId: 0,
  semesterTypeId: 0,
  semester: 0,
  course: 0,
  document: null,
  averageGrade: null,
};

const validationSchema = Yup.object().shape({
  nacidEventId: Yup.number().required("Събитието е задължително").moreThan(0, "Моля, изберете събитие"),
  documentNumber: Yup.string().nullable(),
  documentDate: Yup.date().nullable(),
});

const StudentEventDialog = ({ visible, onHide, studentId }: StudentEventDialogProps) => {
  const { data: nacidEvents } = nacidEventsApi.useGetActiveNacidEventsQuery();
  const [insertStudentEvent, { isLoading }] = nacidEventsApi.useInsertStudentHistoryEventAndSendToNacidMutation();
  const toast = useRef<Toast>(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const request: StudentEventRequest = {
          ...values,
          studentId,
        };

        const response = await insertStudentEvent(request).unwrap();

        if (response) {
          toast.current?.show({ severity: "success", summary: "Успех", detail: "Събитието е добавено успешно" });
          formik.resetForm();
          onHide();
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Грешка",
            detail: "Възникна грешка при добавяне на събитието",
          });
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Грешка",
          detail: "Възникна грешка при добавяне на събитието",
        });
        console.error("Error submitting form:", error);
      }
    },
  });

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Добавяне на събитие"
        visible={visible}
        onHide={onHide}
        style={{ width: "50vw" }}
        modal
        footer={null}
      >
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className="p-fluid formgrid grid">
              <div className="field col-12">
                <CustomDropdown<NacidEventResponse>
                  label="НАЦИД Събитие"
                  id="nacidEventId"
                  options={nacidEvents?.data || []}
                  optionLabel={(option) => `${option.statusCode} - ${option.name} (${option.nacidEventId})`}
                  optionValue="nacidEventId"
                  placeholder="Изберете събитие"
                  required
                />
              </div>
              <div className="field col-12 md:col-6">
                <CustomInputText label="Номер на документ" id="documentNumber" />
              </div>
              <div className="field col-12 md:col-6">
                <CustomDatepicker label="Дата на документ" id="documentDate" />
              </div>
            </div>
            <div className="flex justify-content-end mt-4">
              <Button
                label="Отказ"
                icon="pi pi-times"
                outlined
                onClick={onHide}
                className="mr-2"
                disabled={isLoading}
                type="button"
              />
              <Button label="Запази" icon="pi pi-check" type="submit" autoFocus loading={isLoading} />
            </div>
          </form>
        </FormikProvider>
      </Dialog>
    </>
  );
};

export default StudentEventDialog;
